/**
 * BulkCoaching — Iter 159.7 (Danny Phase 3.2).
 *
 * Manager-only page. Pick reps + a window → generate per-rep coaching
 * drafts from their recent call activity → review/edit each draft →
 * "Send to rep" dispatches it as a weekly_summary feedback row that
 * appears in the rep's existing MyCoachingFeedbackCard + notification.
 *
 * Reuses the existing rep-ai-summary engine on the backend (no new LLM
 * pipeline). Rate-limited server-side: one weekly summary per rep per
 * 24h to keep the LLM bill predictable and avoid spam.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Sparkles, Send, RefreshCw, CheckCircle2, AlertCircle, Users, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = new Set(["super_admin", "crm_admin", "sales_manager", "marketing_manager"]);

const PERIODS = [
  { key: 7,  label: "7 days" },
  { key: 14, label: "14 days" },
  { key: 30, label: "30 days" },
];

export default function BulkCoaching() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isManager = MANAGER_ROLES.has((user?.role || "").toLowerCase());

  const [days, setDays] = useState(7);
  const [reps, setReps] = useState([]);          // [{agent_id, agent_name, calls, connects, connect_rate}]
  const [selected, setSelected] = useState(new Set());
  const [loadingReps, setLoadingReps] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [cards, setCards] = useState([]);        // generated drafts (one per rep)

  // Load rep list with activity in the current window
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      setLoadingReps(true);
      try {
        const { data } = await axios.get(`${API}/admin/rep-activity?days=${days}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        const rollup = (Array.isArray(data?.rollup) ? data.rollup : []).filter((r) => (r.calls || 0) > 0);
        setReps(rollup);
        // Pre-check anyone with >=10 calls in the window (active enough to coach)
        setSelected(new Set(rollup.filter((r) => (r.calls || 0) >= 10).map((r) => r.agent_id)));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load rep activity");
      } finally {
        if (!cancelled) setLoadingReps(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, token, isManager]);

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => {
    if (selected.size === reps.length) setSelected(new Set());
    else setSelected(new Set(reps.map((r) => r.agent_id)));
  };

  const generateDrafts = async () => {
    if (selected.size === 0) { toast.error("Select at least one rep"); return; }
    setGenerating(true);
    setCards([]);
    try {
      const { data } = await axios.post(`${API}/admin/bulk-coaching/generate`,
        { rep_ids: Array.from(selected), days },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const generated = (data?.coaching || []).map((c) => ({
        ...c,
        edited_text: c.draft_text || "",
        status: c.error ? "error" : "draft",   // draft | sending | sent | error | skipped
        last_error: c.error,
      }));
      setCards(generated);
      toast.success(`Generated ${generated.length} draft${generated.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    } finally {
      setGenerating(false);
    }
  };

  const sendOne = async (idx) => {
    setCards((cs) => cs.map((c, i) => i === idx ? { ...c, status: "sending" } : c));
    const card = cards[idx];
    try {
      const { data } = await axios.post(`${API}/admin/bulk-coaching/dispatch`,
        { items: [{ agent_id: card.agent_id, text: card.edited_text }], days },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const skipped = (data?.skipped || []).find((s) => s.agent_id === card.agent_id);
      if (skipped) {
        setCards((cs) => cs.map((c, i) => i === idx ? { ...c, status: "skipped", last_error: skipped.reason } : c));
        toast.warning(`${card.agent_name}: ${skipped.reason}`);
      } else {
        setCards((cs) => cs.map((c, i) => i === idx ? { ...c, status: "sent" } : c));
        toast.success(`Sent to ${card.agent_name}`);
      }
    } catch (e) {
      setCards((cs) => cs.map((c, i) => i === idx ? { ...c, status: "error", last_error: e?.response?.data?.detail || e.message } : c));
      toast.error(`${card.agent_name}: ${e?.response?.data?.detail || "Send failed"}`);
    }
  };

  const sendAll = async () => {
    const pending = cards.map((c, i) => ({ c, i })).filter(({ c }) => c.status === "draft" && c.edited_text.trim());
    if (pending.length === 0) { toast.error("No drafts ready to send"); return; }
    setCards((cs) => cs.map((c) => c.status === "draft" && c.edited_text.trim() ? { ...c, status: "sending" } : c));
    try {
      const items = pending.map(({ c }) => ({ agent_id: c.agent_id, text: c.edited_text }));
      const { data } = await axios.post(`${API}/admin/bulk-coaching/dispatch`,
        { items, days },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const sentSet = new Set((data?.sent || []).map((s) => s.agent_id));
      const skippedMap = new Map((data?.skipped || []).map((s) => [s.agent_id, s.reason]));
      setCards((cs) => cs.map((c) => {
        if (sentSet.has(c.agent_id)) return { ...c, status: "sent" };
        if (skippedMap.has(c.agent_id)) return { ...c, status: "skipped", last_error: skippedMap.get(c.agent_id) };
        return c;
      }));
      toast.success(`Sent ${data?.sent_count || 0} · Skipped ${data?.skipped_count || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch send failed");
    }
  };

  const updateText = (idx, val) => setCards((cs) => cs.map((c, i) => i === idx ? { ...c, edited_text: val } : c));

  const draftsReady = useMemo(() => cards.filter((c) => c.status === "draft" && c.edited_text.trim()).length, [cards]);

  if (!isManager) {
    return (
      <div style={{ padding: 24 }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Manager access required</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
            Bulk coaching is only available to super_admin, crm_admin, sales_manager, and marketing_manager roles.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto", fontFamily: "var(--font)" }} data-testid="bulk-coaching-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate(-1)} className="btn" style={{ padding: "6px 8px", fontSize: 11 }} data-testid="bulk-coaching-back"><ArrowLeft style={{ width: 12, height: 12 }} /></button>
          <Sparkles style={{ width: 16, height: 16, color: "#7C3AED" }} />
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Bulk coaching</h1>
        </div>
        <div style={{ display: "flex", gap: 4 }} data-testid="bulk-coaching-period">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setDays(p.key)} data-testid={`bulk-period-${p.key}`}
              style={{ padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: days === p.key ? 600 : 400, background: days === p.key ? "#1b365D" : "var(--gray-light, #f4f4f5)", color: days === p.key ? "#fff" : "var(--text-tertiary, #6E6E72)", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "0 0 16px" }}>
        Generates personalized coaching drafts from each rep's recent call activity. Edit before sending — each rep gets one summary per 24 hours.
      </p>

      {/* Step 1: Rep picker */}
      <div className="card" style={{ padding: 14, marginBottom: 12 }} data-testid="bulk-coaching-rep-picker">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Users style={{ width: 13, height: 13, color: "var(--text-tertiary)" }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>Select reps to coach</span>
            <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>({selected.size} of {reps.length} selected)</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={toggleAll} className="btn" style={{ fontSize: 11, padding: "4px 10px" }} data-testid="bulk-toggle-all">
              {selected.size === reps.length ? "Deselect all" : "Select all"}
            </button>
            <button onClick={generateDrafts} disabled={generating || selected.size === 0} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 5 }} data-testid="bulk-generate">
              {generating ? <RefreshCw style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
              {generating ? "Generating…" : "Generate drafts"}
            </button>
          </div>
        </div>
        {loadingReps ? (
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "12px 4px" }}>Loading reps…</div>
        ) : reps.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "12px 4px" }}>No reps have placed calls in this window.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
            {reps.map((r) => {
              const checked = selected.has(r.agent_id);
              return (
                <label key={r.agent_id} data-testid={`bulk-rep-${r.agent_id}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, border: checked ? "1px solid #1b365D" : "0.5px solid #eee", background: checked ? "rgba(27,54,93,0.04)" : "#fff", cursor: "pointer", fontSize: 12 }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(r.agent_id)} data-testid={`bulk-rep-check-${r.agent_id}`} />
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.agent_name}</div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                      {r.calls || 0} calls · {r.connect_rate || 0}% connect
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Step 2: Drafts */}
      {cards.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Drafts ({draftsReady} ready)</div>
            <button onClick={sendAll} disabled={draftsReady === 0} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 5 }} data-testid="bulk-send-all">
              <Send style={{ width: 11, height: 11 }} /> Send all ready ({draftsReady})
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {cards.map((c, idx) => (
              <CoachingCard key={c.agent_id} card={c} idx={idx} onChange={updateText} onSend={sendOne} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CoachingCard({ card, idx, onChange, onSend }) {
  const isError = card.status === "error" && !card.draft_text;
  const isSent = card.status === "sent";
  const isSkipped = card.status === "skipped";
  const isSending = card.status === "sending";
  const stats = card.stats || {};
  return (
    <div className="card" style={{ padding: 14, opacity: isSent ? 0.65 : 1 }} data-testid={`bulk-card-${card.agent_id}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{card.agent_name}</span>
          {stats.calls > 0 && (
            <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
              {stats.calls} calls · {stats.connect_rate || 0}% · {stats.connects || 0} connects
            </span>
          )}
        </div>
        <StatusBadge status={card.status} error={card.last_error} />
      </div>

      {isError ? (
        <div style={{ fontSize: 12, color: "#A32D2D", padding: "8px 0" }}>Couldn't generate: {card.last_error}</div>
      ) : (
        <>
          {(card.highlights || []).length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {(card.highlights || []).slice(0, 3).map((h, i) => (
                <span key={i} style={{ fontSize: 10, padding: "2px 8px", background: "rgba(34,197,94,0.08)", color: "#166534", borderRadius: 12 }}>✓ {h}</span>
              ))}
              {(card.risks || []).slice(0, 2).map((h, i) => (
                <span key={`r${i}`} style={{ fontSize: 10, padding: "2px 8px", background: "rgba(220,38,38,0.08)", color: "#B91C1C", borderRadius: 12 }}>⚠ {h}</span>
              ))}
            </div>
          )}
          <textarea
            value={card.edited_text}
            onChange={(e) => onChange(idx, e.target.value)}
            disabled={isSent || isSending}
            data-testid={`bulk-card-text-${card.agent_id}`}
            className="input"
            rows={Math.max(6, Math.min(14, (card.edited_text || "").split("\n").length + 1))}
            style={{ width: "100%", resize: "vertical", fontFamily: "var(--font)", fontSize: 12, lineHeight: 1.55 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button
              onClick={() => onSend(idx)}
              disabled={isSent || isSending || isSkipped || !card.edited_text.trim()}
              className="btn btn-primary"
              style={{ fontSize: 11, padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}
              data-testid={`bulk-card-send-${card.agent_id}`}
            >
              {isSending ? <RefreshCw style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Send style={{ width: 11, height: 11 }} />}
              {isSent ? "Sent" : isSkipped ? "Skipped" : isSending ? "Sending…" : "Send to rep"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status, error }) {
  if (status === "sent") return <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, background: "#dcfce7", color: "#166534", display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle2 style={{ width: 10, height: 10 }} />Sent</span>;
  if (status === "skipped") return <span title={error || ""} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, background: "#fef9c3", color: "#92400e", display: "inline-flex", alignItems: "center", gap: 4 }}><AlertCircle style={{ width: 10, height: 10 }} />Cooldown</span>;
  if (status === "error") return <span title={error || ""} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, background: "#fee2e2", color: "#991b1b", display: "inline-flex", alignItems: "center", gap: 4 }}><AlertCircle style={{ width: 10, height: 10 }} />Error</span>;
  return null;
}
